import React from "react";

/**
 * measurement.ts — the ONE place that decides whether a rule's numbers may be displayed.
 *
 * Why this exists: several panels rendered `violations of applicable` straight from the rule. When a rule
 * had not been executed, could not be compiled, or had been revised since it was last measured, those
 * fields still held the PREVIOUS values — so the UI confidently showed a number that was not true of the
 * rule on screen. Every place that shows an impact figure must go through `measurementOf()`; if it does
 * not return state "measured", callers must render <NotMeasured/> instead of a number.
 */

export type MeasurementState =
  | "measured"        // ran against this exact version — numbers are real
  | "stale"           // rule was revised after it was measured
  | "not_compiled"    // the engine cannot build an executable check from this rule
  | "needs_values"    // a value rule with no governed list
  | "no_rows"         // ran, but nothing matched
  | "error"           // execution failed
  | "not_run";        // never executed

export interface Measurement {
  state: MeasurementState;
  ok: boolean;                 // true ONLY when a number may be shown
  applicable?: number | null;
  violations?: number | null;
  ratePct?: number | null;
  passPct?: number | null;
  label: string;               // short badge text
  reason: string;              // why there is no number, in plain language
  previous?: { applicable?: number | null; violations?: number | null } | null;
  actionable: boolean;         // true when the SME can do something about it
}

const N = (v: any) => (typeof v === "number" && Number.isFinite(v) ? v : null);

export function measurementOf(rule: any): Measurement {
  const ia = rule?.impact_assessment || rule?._raw?.impact_assessment || {};
  const tag = rule?.tags?.execution_state as string | undefined;
  const note = rule?.tag_notes?.execution || ia.note || ia.reason || ia.error || "";
  const appl = N(ia.applicable_rows ?? ia.applicable);
  const viol = N(ia.violations ?? ia.violating_rows);
  const prev = ia.previous ? { applicable: N(ia.previous.applicable_rows), violations: N(ia.previous.violations) } : null;
  const status = String(ia.status || "").toUpperCase();

  const no = (state: MeasurementState, label: string, reason: string, actionable = false): Measurement =>
    ({ state, ok: false, label, reason, previous: prev, actionable, applicable: null, violations: null });

  if (status === "STALE" || tag === "not_measured_stale")
    return no("stale", "Not measured for this version",
      note || "This rule was revised after it was last measured, so the stored numbers describe the earlier version.",
      true);
  if (status === "NOT_COMPILED" || tag === "not_measured_uncompilable")
    return no("not_compiled", "Cannot be executed",
      note || "The engine cannot build an executable check from this rule, so it has never run against your data.");
  if (tag === "not_measured_needs_values")
    return no("needs_values", "Needs governed values",
      note || "This is a value rule with no governed list, so it cannot be measured meaningfully.", true);
  if (tag === "not_measured_error" || status === "ERROR")
    return no("error", "Execution error", note || "The rule failed to execute.");
  if (tag === "not_measured_no_rows")
    return no("no_rows", "No matching rows", note || "The rule ran, but its condition matched no records.");
  if (ia.executed !== true || appl == null || viol == null)
    return no("not_run", "Not measured yet",
      note || "This rule has not been executed against your data yet.", true);

  const ratePct = appl ? Math.round((viol / appl) * 10000) / 100 : 0;
  return {
    state: "measured", ok: true, applicable: appl, violations: viol,
    ratePct, passPct: Math.round((100 - ratePct) * 100) / 100,
    label: viol > 0 ? "Flags data" : "Passed (clean)",
    reason: "", previous: null, actionable: false,
  };
}

/** Renders in place of any impact figure when the numbers are not trustworthy. */
export function NotMeasured({ m, compact }: { m: Measurement; compact?: boolean }) {
  const tone = m.state === "error" ? "err" : m.state === "stale" ? "warn" : "info";
  return (
    <div className={`nm nm-${tone}${compact ? " compact" : ""}`}>
      <style>{CSS}</style>
      <span className="t">{m.state === "error" ? "⚠" : m.state === "stale" ? "↻" : "—"} {m.label}</span>
      {!compact && <span className="r">{m.reason}</span>}
      {!compact && m.previous?.applicable != null && (
        <span className="p">
          Previously (earlier version): {(m.previous.violations ?? 0).toLocaleString()} of{" "}
          {(m.previous.applicable ?? 0).toLocaleString()} — shown for history only, not this rule.
        </span>
      )}
    </div>
  );
}

/** "1,234 of 26,798 (4.6%)" — only ever called when m.ok is true. */
export function impactText(m: Measurement): string {
  if (!m.ok) return m.label;
  return `${(m.violations ?? 0).toLocaleString()} of ${(m.applicable ?? 0).toLocaleString()}` +
         (m.ratePct != null ? ` (${m.ratePct}%)` : "");
}

const CSS = `
.nm{display:flex;flex-direction:column;gap:3px;border-radius:9px;padding:9px 12px;font-size:12.5px;line-height:1.5}
.nm.compact{padding:3px 9px;border-radius:20px;display:inline-flex;flex-direction:row;font-size:11px}
.nm .t{font-weight:700}
.nm .r{opacity:.9}
.nm .p{font-size:11.5px;opacity:.75;font-family:ui-monospace,monospace}
.nm-info{background:#eef1f5;color:#4a5763;border:1px solid #dde3ea}
.nm-warn{background:#fbf6ea;color:#8a6516;border:1px solid #ead9b4}
.nm-err{background:#fdeceb;color:#b23a30;border:1px solid #f0cdc8}
`;
